/**
 * PipelineStatusChevron — live status of each sourcing-pipeline stage.
 *
 * Renders a horizontal chevron with one tile per stage. Each tile shows:
 *   - stage name + tiny description
 *   - colored status badge (ok/running/warning/stale/paused/failed)
 *   - human-readable last-ran / running-now message
 *   - next-scheduled time (when applicable)
 *   - error_message (red) if the stage reported a failure
 *
 * Polls `/api/admin/ops/sourcing/status` every 30s. Stages flow left → right:
 *
 *   Apollo  >  Phantombuster  >  Drafter  >  Gmail sweep
 *
 * Brand-aligned: green for healthy, amber for stuck-but-not-broken, rose for
 * actual failures. Uses Fraunces only for the headline (per CLAUDE.md rules);
 * tile content is DM Sans.
 *
 * Built 2026-05-19 — pairs with backend/routes/admin_ops_sourcing.py
 * :get_pipeline_status.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Search, Globe, MailPlus, Inbox,
  CheckCircle2, AlertTriangle, AlertCircle, PauseCircle,
  Loader2, Clock,
} from "lucide-react";

import { getAdminToken } from "@/lib/authStorage";
import { API } from "@/App";

const REFRESH_INTERVAL_MS = 30_000;

// Stage key → icon. Falls back to a circle if unknown.
const STAGE_ICONS = {
  apollo:        Search,
  phantombuster: Globe,
  drafter:       MailPlus,
  gmail_sweep:   Inbox,
};

// Status → {bg, text, border, label, Icon, pulse}. Tuned for the brand palette.
const STATUS_STYLES = {
  ok: {
    badge: "bg-emerald-50 text-emerald-800 border-emerald-200",
    dot:   "bg-emerald-500",
    Icon:  CheckCircle2,
    label: "OK",
    pulse: false,
  },
  running: {
    badge: "bg-sky-50 text-sky-800 border-sky-200",
    dot:   "bg-sky-500",
    Icon:  Loader2,
    label: "Running",
    pulse: true,    // animate the icon
  },
  warning: {
    badge: "bg-amber-50 text-amber-800 border-amber-200",
    dot:   "bg-amber-500",
    Icon:  AlertTriangle,
    label: "Warning",
    pulse: false,
  },
  stale: {
    badge: "bg-zinc-100 text-zinc-600 border-zinc-200",
    dot:   "bg-zinc-400",
    Icon:  Clock,
    label: "Stale",
    pulse: false,
  },
  paused: {
    badge: "bg-zinc-100 text-zinc-700 border-zinc-300",
    dot:   "bg-zinc-500",
    Icon:  PauseCircle,
    label: "Paused",
    pulse: false,
  },
  failed: {
    badge: "bg-rose-50 text-rose-800 border-rose-200",
    dot:   "bg-rose-500",
    Icon:  AlertCircle,
    label: "Failed",
    pulse: false,
  },
};

function authHeaders() {
  const t = getAdminToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function formatNextRun(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    // Local time, short — "today 9:00 AM" / "tomorrow 9:00 AM"
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    const opts = { hour: "numeric", minute: "2-digit" };
    return `${sameDay ? "today" : "tomorrow"} ${d.toLocaleTimeString([], opts)}`;
  } catch {
    return null;
  }
}

function StageTile({ stage, isFirst, isLast }) {
  const styles = STATUS_STYLES[stage.status] || STATUS_STYLES.stale;
  const StageIcon = STAGE_ICONS[stage.key] || CheckCircle2;
  const StatusIcon = styles.Icon;
  const nextRun = formatNextRun(stage.next_scheduled_at);

  return (
    <div className="relative flex-1 min-w-[200px]">
      {/* Chevron connector — small triangle on the right */}
      {!isLast && (
        <div
          aria-hidden="true"
          className="absolute right-[-7px] top-1/2 -translate-y-1/2 w-0 h-0 z-10"
          style={{
            borderTop:    "10px solid transparent",
            borderBottom: "10px solid transparent",
            borderLeft:   "10px solid #E4E4E7",  // zinc-200
          }}
        />
      )}

      <div className={`
        h-full border bg-white px-4 py-3
        ${isFirst ? "rounded-l-md" : ""}
        ${isLast ? "rounded-r-md" : ""}
        ${!isFirst && !isLast ? "" : ""}
        border-zinc-200
        ${stage.status === "running" ? "ring-2 ring-sky-200" : ""}
      `}>
        {/* Row 1: icon + label + dot */}
        <div className="flex items-center gap-2 mb-1.5">
          <StageIcon className="w-4 h-4 text-zinc-500 shrink-0" />
          <span className="text-sm font-medium text-[#0E1410] truncate">
            {stage.label}
          </span>
          <span className={`ml-auto w-2 h-2 rounded-full ${styles.dot} ${styles.pulse ? "animate-pulse" : ""}`} />
        </div>

        {/* Row 2: status badge */}
        <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border ${styles.badge} mb-2`}>
          <StatusIcon className={`w-3 h-3 ${styles.pulse ? "animate-spin" : ""}`} />
          {styles.label}
        </div>

        {/* Row 3: message */}
        <div className="text-xs text-zinc-700 leading-tight mb-1.5">
          {stage.status_message}
        </div>

        {/* Row 4: next scheduled */}
        {nextRun && (
          <div className="text-[10px] text-zinc-400">
            Next: {nextRun}
          </div>
        )}

        {/* Row 5: error_message in red */}
        {stage.error_message && (
          <div className="mt-2 text-[10px] text-rose-700 leading-snug border-t border-rose-100 pt-1.5">
            {stage.error_message}
          </div>
        )}
      </div>
    </div>
  );
}

export default function PipelineStatusChevron() {
  const [stages, setStages] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const timer = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/ops/sourcing/status`, { headers: authHeaders() });
      setStages(r.data.stages || []);
      setGeneratedAt(r.data.generated_at);
      setErr(null);
    } catch (e) {
      // Swallow auth errors silently — parent page handles redirect.
      if (e?.response?.status !== 401) {
        setErr(e?.message || "Failed to load pipeline status");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();  // initial fetch
    timer.current = setInterval(fetchStatus, REFRESH_INTERVAL_MS);
    return () => clearInterval(timer.current);
  }, [fetchStatus]);

  if (loading && stages.length === 0) {
    return (
      <div className="rounded-md border border-zinc-200 bg-white px-6 py-8 text-sm text-zinc-500">
        Loading pipeline status…
      </div>
    );
  }

  return (
    <section className="mb-10">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-serif text-2xl">Pipeline status</h2>
        <div className="text-xs text-zinc-400">
          {generatedAt ? `Refreshed ${new Date(generatedAt).toLocaleTimeString()}` : ""}
          <button
            onClick={fetchStatus}
            className="ml-3 text-[#17803D] hover:underline"
          >
            Refresh now
          </button>
        </div>
      </div>

      {err && (
        <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-xs text-rose-800">
          {err}
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-1 md:gap-0">
        {stages.map((s, i) => (
          <StageTile
            key={s.key}
            stage={s}
            isFirst={i === 0}
            isLast={i === stages.length - 1}
          />
        ))}
      </div>
    </section>
  );
}
